import { useRef, useState } from "react";
import { Plus, Search, ShieldCheck, TriangleAlert, Users } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierDispatchCenter,
  type CarrierTeamInvite,
  type CarrierTeamMember,
  type CarrierTeamMembership,
  useCarrierDispatchCenters,
  useCarrierTeam,
  useCarrierTeamInvites,
  useDeleteCarrierInvite,
  useResendCarrierInvite,
  useUpdateCarrierTeamMember,
} from "@/api/carrier";
import {
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InviteTeamMemberDialog } from "@/features/invite-team-member-dialog";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDateOnly } from "@/lib/format";

const ROLE_LABEL: Record<string, string> = {
  CARRIER_OWNER: "Carrier Admin",
  CARRIER_DISPATCHER: "Dispatcher",
  OPERATOR: "Operator",
};

type MembershipUpdate = Parameters<
  ReturnType<typeof useUpdateCarrierTeamMember>["mutateAsync"]
>[0];

interface MembershipUpdateRequest {
  vars: MembershipUpdate;
  successMessage: string;
  title: string;
  description: string;
  confirmLabel: string;
  pendingLabel: string;
  destructive?: boolean;
}

type RequestMembershipUpdate = (request: MembershipUpdateRequest) => void;

interface AccessConfirmation {
  kind: "member" | "invite";
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  pendingLabel: string;
  destructive: boolean;
  action: () => Promise<boolean>;
}

interface MemberEntry {
  member: CarrierTeamMember;
  membership: CarrierTeamMembership;
}

export default function TeamSettingsScreen() {
  const teamPager = usePagedState();
  const invitePager = usePagedState();
  const team = useCarrierTeam({
    page: teamPager.page,
    limit: teamPager.pageSize,
    q: teamPager.search,
    status: "ALL",
    active: "all",
  });
  const invites = useCarrierTeamInvites({
    page: invitePager.page,
    limit: invitePager.pageSize,
    q: invitePager.search,
    status: "PENDING",
  });
  const centers = useCarrierDispatchCenters();
  const revoke = useDeleteCarrierInvite();
  const resend = useResendCarrierInvite();
  const updateMember = useUpdateCarrierTeamMember();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [viewingMembershipId, setViewingMembershipId] = useState<string | null>(
    null
  );
  const [viewingInviteId, setViewingInviteId] = useState<string | null>(null);
  const [memberMutationTarget, setMemberMutationTarget] = useState<
    string | null
  >(null);
  const [memberMutationError, setMemberMutationError] = useState<string | null>(
    null
  );
  const [inviteMutationTarget, setInviteMutationTarget] = useState<
    string | null
  >(null);
  const [inviteMutationError, setInviteMutationError] = useState<string | null>(
    null
  );
  const [confirmation, setConfirmation] = useState<AccessConfirmation | null>(
    null
  );
  const [confirmationPending, setConfirmationPending] = useState(false);
  const memberMutationLock = useRef(false);
  const inviteMutationLock = useRef(false);
  const confirmationLock = useRef(false);

  const teamRows: CarrierTeamMember[] = Array.isArray(team.data)
    ? (team.data as CarrierTeamMember[])
    : (team.data?.data ?? []);
  const teamMetadata = Array.isArray(team.data) ? null : team.data?.metadata;
  const memberEntries = teamRows.flatMap((member) =>
    member.memberships.map((membership) => ({ member, membership }))
  );
  const inviteRows: CarrierTeamInvite[] = (
    Array.isArray(invites.data)
      ? (invites.data as CarrierTeamInvite[])
      : (invites.data?.data ?? [])
  ).filter((invite) => !invite.acceptedAt);
  const inviteMetadata = Array.isArray(invites.data)
    ? null
    : invites.data?.metadata;
  const viewingMember = memberEntries.find(
    (entry) => entry.membership.id === viewingMembershipId
  );
  const viewingInvite = inviteRows.find(
    (invite) => invite.id === viewingInviteId
  );
  const teamForbidden = hasHttpStatus(team.error, 403);
  const invitesForbidden = hasHttpStatus(invites.error, 403);
  const forbidden = teamForbidden || invitesForbidden;

  const updateMembership = async (
    vars: MembershipUpdate,
    successMessage: string
  ) => {
    if (memberMutationLock.current) return false;
    memberMutationLock.current = true;
    setMemberMutationTarget(vars.membershipId);
    setMemberMutationError(null);
    try {
      await updateMember.mutateAsync(vars);
      toast.success(successMessage);
      return true;
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to manage team access."
        : (response.response?.data?.error ??
          "Team member could not be updated.");
      setMemberMutationError(message);
      toast.error(message);
      return false;
    } finally {
      memberMutationLock.current = false;
      setMemberMutationTarget(null);
    }
  };

  const mutateInvite = async (
    kind: "resend" | "revoke",
    invite: CarrierTeamInvite
  ) => {
    if (inviteMutationLock.current) return false;
    inviteMutationLock.current = true;
    setInviteMutationTarget(`${kind}:${invite.id}`);
    setInviteMutationError(null);
    try {
      if (kind === "resend") {
        await resend.mutateAsync(invite.id);
        toast.success("Invite resent.");
      } else {
        await revoke.mutateAsync(invite.id);
        toast.success("Invite revoked.");
      }
      return true;
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to manage team invitations."
        : (response.response?.data?.error ??
          `Invite could not be ${kind === "resend" ? "resent" : "revoked"}.`);
      setInviteMutationError(message);
      toast.error(message);
      return false;
    } finally {
      inviteMutationLock.current = false;
      setInviteMutationTarget(null);
    }
  };

  const requestMembershipUpdate: RequestMembershipUpdate = (request) => {
    if (memberMutationLock.current || confirmationLock.current) return;
    setMemberMutationError(null);
    setConfirmation({
      kind: "member",
      title: request.title,
      description: request.description,
      confirmLabel: request.confirmLabel,
      cancelLabel: "Keep current",
      pendingLabel: request.pendingLabel,
      destructive: request.destructive ?? false,
      action: () => updateMembership(request.vars, request.successMessage),
    });
  };

  const requestInviteRevoke = (
    invite: CarrierTeamInvite,
    closeDetailOnSuccess = false
  ) => {
    if (inviteMutationLock.current || confirmationLock.current) return;
    setInviteMutationError(null);
    setConfirmation({
      kind: "invite",
      title: "Revoke invitation?",
      description: `Revoke the invitation for ${invite.email}?`,
      confirmLabel: "Revoke invitation",
      cancelLabel: "Keep invitation",
      pendingLabel: `Revoking invitation for ${invite.email}…`,
      destructive: true,
      action: async () => {
        const succeeded = await mutateInvite("revoke", invite);
        if (succeeded && closeDetailOnSuccess) setViewingInviteId(null);
        return succeeded;
      },
    });
  };

  const confirmAccessChange = async () => {
    if (!confirmation || confirmationLock.current) return;
    confirmationLock.current = true;
    setConfirmationPending(true);
    try {
      if (await confirmation.action()) setConfirmation(null);
    } finally {
      confirmationLock.current = false;
      setConfirmationPending(false);
    }
  };

  return (
    <div className="carrier-page">
      <PageHeader
        title="Users & access"
        actions={
          !forbidden ? (
            <Button size="touch" onClick={() => setInviteOpen(true)}>
              <Plus className="h-4 w-4" />
              Invite team member
            </Button>
          ) : undefined
        }
      />

      <section aria-labelledby="team-members-heading" className="space-y-3">
        <SectionHeading
          id="team-members-heading"
          icon={<Users className="h-4 w-4" />}
          title="Team members"
          description={`${teamMetadata?.total ?? memberEntries.length} memberships`}
        />
        <Card className="overflow-hidden">
          <div className="border-b p-3">
            <TableToolbar
              className="mb-0"
              search={teamPager.search}
              onSearchChange={teamPager.setSearch}
              searchPlaceholder="Search members…"
              searchAriaLabel="Search team members"
            />
          </div>
          {memberMutationError && (
            <MutationError message={memberMutationError} />
          )}
          {team.error && teamRows.length > 0 && !teamForbidden && (
            <CachedWarning
              message="Member refresh failed. Loaded membership data remains available."
              onRetry={() => void team.refetch()}
            />
          )}
          {teamForbidden ? (
            <ListError
              forbidden
              subject="team members"
              onRetry={() => void team.refetch()}
            />
          ) : team.isLoading && teamRows.length === 0 ? (
            <ListLoading label="Loading team members" />
          ) : team.error && teamRows.length === 0 ? (
            <ListError
              forbidden={hasHttpStatus(team.error, 403)}
              subject="team members"
              onRetry={() => void team.refetch()}
            />
          ) : memberEntries.length === 0 ? (
            <EmptyList
              icon={<Search />}
              message={
                teamPager.search.trim()
                  ? `No team members match “${teamPager.search.trim()}”.`
                  : "No team members found."
              }
            />
          ) : (
            <>
              <div className="space-y-2 p-3 md:hidden">
                {memberEntries.map((entry) => (
                  <MemberCard
                    key={entry.membership.id}
                    entry={entry}
                    pending={memberMutationTarget === entry.membership.id}
                    onView={() => setViewingMembershipId(entry.membership.id)}
                  />
                ))}
              </div>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Dispatch center</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Membership</TableHead>
                      <TableHead className="w-12" aria-label="Actions" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberEntries.map((entry) => {
                      const pending =
                        memberMutationTarget === entry.membership.id;
                      return (
                        <TableRow
                          key={entry.membership.id}
                          aria-busy={pending || undefined}
                        >
                          <TableCell className="font-medium">
                            {entry.member.name ?? "Unnamed member"}
                          </TableCell>
                          <TableCell>{entry.member.email}</TableCell>
                          <TableCell>
                            <RoleControl
                              entry={entry}
                              disabled={memberMutationTarget !== null}
                              onRequestUpdate={requestMembershipUpdate}
                            />
                          </TableCell>
                          <TableCell>
                            <CenterControl
                              entry={entry}
                              centers={centers.data ?? []}
                              centersLoading={centers.isLoading}
                              centersError={centers.error}
                              disabled={memberMutationTarget !== null}
                              onRequestUpdate={requestMembershipUpdate}
                            />
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={
                                entry.member.active ? "ACTIVE" : "INACTIVE"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={entry.membership.status} />
                          </TableCell>
                          <TableCell>
                            {pending && (
                              <span role="status" className="sr-only">
                                Saving access changes for {entry.member.email}…
                              </span>
                            )}
                            <MemberActions
                              entry={entry}
                              disabled={memberMutationTarget !== null}
                              onRequestUpdate={requestMembershipUpdate}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          {teamMetadata && (
            <div className="border-t p-3">
              <TablePager
                page={teamMetadata.page}
                pageSize={teamMetadata.limit}
                total={teamMetadata.total}
                totalPages={teamMetadata.totalPages}
                onPageChange={teamPager.setPage}
                onPageSizeChange={teamPager.setPageSize}
                disabled={team.isLoading}
              />
            </div>
          )}
        </Card>
      </section>

      <section aria-labelledby="pending-invites-heading" className="space-y-3">
        <SectionHeading
          id="pending-invites-heading"
          icon={<ShieldCheck className="h-4 w-4" />}
          title="Pending invites"
          description={`${inviteMetadata?.total ?? inviteRows.length} awaiting acceptance`}
        />
        <Card className="overflow-hidden">
          <div className="border-b p-3">
            <TableToolbar
              className="mb-0"
              search={invitePager.search}
              onSearchChange={invitePager.setSearch}
              searchPlaceholder="Search invites…"
              searchAriaLabel="Search pending invites"
            />
          </div>
          {inviteMutationError && (
            <MutationError message={inviteMutationError} />
          )}
          {invites.error && inviteRows.length > 0 && !invitesForbidden && (
            <CachedWarning
              message="Invite refresh failed. Loaded pending invitations remain available."
              onRetry={() => void invites.refetch()}
            />
          )}
          {invitesForbidden ? (
            <ListError
              forbidden
              subject="pending invitations"
              onRetry={() => void invites.refetch()}
            />
          ) : invites.isLoading && inviteRows.length === 0 ? (
            <ListLoading label="Loading pending invites" />
          ) : invites.error && inviteRows.length === 0 ? (
            <ListError
              forbidden={hasHttpStatus(invites.error, 403)}
              subject="pending invitations"
              onRetry={() => void invites.refetch()}
            />
          ) : inviteRows.length === 0 ? (
            <EmptyList
              icon={<ShieldCheck />}
              message={
                invitePager.search.trim()
                  ? `No pending invites match “${invitePager.search.trim()}”.`
                  : "No pending invites."
              }
            />
          ) : (
            <>
              <div className="space-y-2 p-3 md:hidden">
                {inviteRows.map((invite) => (
                  <InviteCard
                    key={invite.id}
                    invite={invite}
                    pending={inviteMutationTarget?.endsWith(invite.id) ?? false}
                    onView={() => setViewingInviteId(invite.id)}
                  />
                ))}
              </div>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role and access</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="w-12" aria-label="Actions" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inviteRows.map((invite) => {
                      const pending =
                        inviteMutationTarget?.endsWith(invite.id) ?? false;
                      return (
                        <TableRow
                          key={invite.id}
                          aria-busy={pending || undefined}
                        >
                          <TableCell className="font-medium">
                            {invite.email}
                          </TableCell>
                          <TableCell>
                            <div>{roleLabel(invite.role)}</div>
                            <div className="text-xs text-muted-foreground">
                              {invite.dispatchCenter?.name ?? "All centers"}
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatDateOnly(invite.createdAt)}
                          </TableCell>
                          <TableCell>
                            {formatDateOnly(invite.expiresAt)}
                          </TableCell>
                          <TableCell>
                            {pending && (
                              <span role="status" className="sr-only">
                                Updating invitation for {invite.email}…
                              </span>
                            )}
                            <InviteActions
                              invite={invite}
                              disabled={inviteMutationTarget !== null}
                              onResend={() =>
                                void mutateInvite("resend", invite)
                              }
                              onRevoke={() => requestInviteRevoke(invite)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          {inviteMetadata && (
            <div className="border-t p-3">
              <TablePager
                page={inviteMetadata.page}
                pageSize={inviteMetadata.limit}
                total={inviteMetadata.total}
                totalPages={inviteMetadata.totalPages}
                onPageChange={invitePager.setPage}
                onPageSizeChange={invitePager.setPageSize}
                disabled={invites.isLoading}
              />
            </div>
          )}
        </Card>
      </section>

      <InviteTeamMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      {viewingMember && (
        <MemberDetailSheet
          entry={viewingMember}
          centers={centers.data ?? []}
          centersLoading={centers.isLoading}
          centersError={centers.error}
          pending={memberMutationTarget === viewingMember.membership.id}
          blocked={memberMutationTarget !== null}
          mutationError={memberMutationError}
          onClose={() => setViewingMembershipId(null)}
          onRequestUpdate={requestMembershipUpdate}
        />
      )}

      {viewingInvite && (
        <InviteDetailSheet
          invite={viewingInvite}
          pending={inviteMutationTarget?.endsWith(viewingInvite.id) ?? false}
          blocked={inviteMutationTarget !== null}
          mutationError={inviteMutationError}
          onClose={() => setViewingInviteId(null)}
          onResend={() => void mutateInvite("resend", viewingInvite)}
          onRevoke={() => requestInviteRevoke(viewingInvite, true)}
        />
      )}

      {confirmation && (
        <AccessConfirmationDialog
          confirmation={confirmation}
          pending={confirmationPending}
          error={
            confirmation.kind === "member"
              ? memberMutationError
              : inviteMutationError
          }
          onCancel={() => setConfirmation(null)}
          onConfirm={() => void confirmAccessChange()}
        />
      )}
    </div>
  );
}

function RoleControl({
  entry,
  disabled,
  onRequestUpdate,
}: {
  entry: MemberEntry;
  disabled: boolean;
  onRequestUpdate: RequestMembershipUpdate;
}) {
  const { member, membership } = entry;
  if (membership.role === "OPERATOR") return <span>Operator</span>;
  return (
    <NativeSelect
      aria-label={`Role for ${member.email}`}
      density="compact"
      className="min-w-32 text-sm md:text-xs"
      value={membership.role}
      disabled={disabled}
      onChange={(event) => {
        const role = event.target.value as
          | "CARRIER_OWNER"
          | "CARRIER_DISPATCHER";
        onRequestUpdate({
          vars: { membershipId: membership.id, role },
          successMessage: "Role updated.",
          title: "Change team role?",
          description: `Change ${member.email}'s role to ${ROLE_LABEL[role]}?`,
          confirmLabel: "Change role",
          pendingLabel: `Saving access changes for ${member.email}…`,
        });
      }}
    >
      <option value="CARRIER_OWNER">Carrier Admin</option>
      <option value="CARRIER_DISPATCHER">Dispatcher</option>
    </NativeSelect>
  );
}

function CenterControl({
  entry,
  centers,
  centersLoading,
  centersError,
  disabled,
  onRequestUpdate,
}: {
  entry: MemberEntry;
  centers: CarrierDispatchCenter[];
  centersLoading: boolean;
  centersError: unknown;
  disabled: boolean;
  onRequestUpdate: RequestMembershipUpdate;
}) {
  const { member, membership } = entry;
  const centersForbidden = hasHttpStatus(centersError, 403);
  return (
    <div className="space-y-1">
      <NativeSelect
        aria-label={`Dispatch center for ${member.email}`}
        density="compact"
        className="min-w-36 text-sm md:text-xs"
        value={membership.dispatchCenterId ?? ""}
        disabled={
          disabled ||
          centersLoading ||
          centersForbidden ||
          (!!centersError && centers.length === 0)
        }
        onChange={(event) => {
          const dispatchCenterId = event.target.value || null;
          const nextCenter = dispatchCenterId
            ? (centers.find((center) => center.id === dispatchCenterId)?.name ??
              "the selected center")
            : "all centers";
          onRequestUpdate({
            vars: { membershipId: membership.id, dispatchCenterId },
            successMessage: "Dispatch center updated.",
            title: "Change dispatch-center access?",
            description: `Change ${member.email}'s access to ${nextCenter}?`,
            confirmLabel: "Change access",
            pendingLabel: `Saving access changes for ${member.email}…`,
          });
        }}
      >
        <option value="">All centers</option>
        {centers.map((center) => (
          <option key={center.id} value={center.id}>
            {center.name}
          </option>
        ))}
      </NativeSelect>
      {!!centersError && (
        <span className="text-[11px] text-amber-700">
          {centersForbidden
            ? "Dispatch-center permission required"
            : centers.length > 0
              ? "Loaded center list"
              : "Center list unavailable"}
        </span>
      )}
    </div>
  );
}

function MemberActions({
  entry,
  disabled,
  onRequestUpdate,
  expanded = false,
}: {
  entry: MemberEntry;
  disabled: boolean;
  onRequestUpdate: RequestMembershipUpdate;
  expanded?: boolean;
}) {
  const { member, membership } = entry;
  const toggleAccount = () => {
    const activating = !member.active;
    onRequestUpdate({
      vars: { membershipId: membership.id, active: activating },
      successMessage: activating
        ? "Team member activated."
        : "Team member deactivated.",
      title: activating ? "Activate account?" : "Deactivate account?",
      description: activating
        ? `Activate ${member.email}? They will regain portal access.`
        : `Deactivate ${member.email}? They will lose portal access.`,
      confirmLabel: activating ? "Activate account" : "Deactivate account",
      pendingLabel: `Saving access changes for ${member.email}…`,
      destructive: !activating,
    });
  };
  const toggleMembership = () => {
    const enabling = membership.status === "DISABLED";
    onRequestUpdate({
      vars: {
        membershipId: membership.id,
        status: enabling ? "ACTIVE" : "DISABLED",
      },
      successMessage: enabling ? "Membership enabled." : "Membership disabled.",
      title: enabling ? "Enable membership?" : "Disable membership?",
      description: enabling
        ? `Enable ${member.email}'s membership? They will regain portal access.`
        : `Disable ${member.email}'s membership?`,
      confirmLabel: enabling ? "Enable membership" : "Disable membership",
      pendingLabel: `Saving access changes for ${member.email}…`,
      destructive: !enabling,
    });
  };

  if (expanded) {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <Button
          variant="outline"
          size="touch"
          disabled={disabled}
          onClick={toggleAccount}
        >
          {member.active ? "Deactivate account" : "Activate account"}
        </Button>
        <Button
          variant="outline"
          size="touch"
          disabled={disabled}
          onClick={toggleMembership}
        >
          {membership.status === "DISABLED"
            ? "Enable membership"
            : "Disable membership"}
        </Button>
      </div>
    );
  }

  return (
    <RowMenu label={`Actions for ${member.email}`} disabled={disabled}>
      <RowMenuItem onSelect={toggleAccount}>
        {member.active ? "Deactivate account" : "Activate account"}
      </RowMenuItem>
      <RowMenuItem onSelect={toggleMembership}>
        {membership.status === "DISABLED"
          ? "Enable membership"
          : "Disable membership"}
      </RowMenuItem>
    </RowMenu>
  );
}

function MemberCard({
  entry,
  pending,
  onView,
}: {
  entry: MemberEntry;
  pending: boolean;
  onView: () => void;
}) {
  const { member, membership } = entry;
  return (
    <article
      className="rounded-md border bg-background p-3"
      aria-busy={pending || undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">
            {member.name ?? "Unnamed member"}
          </h3>
          <p className="truncate text-xs text-muted-foreground">
            {member.email}
          </p>
        </div>
        <StatusBadge status={accountStatus(member, membership)} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        <span>{roleLabel(membership.role)}</span>
        <span className="text-muted-foreground">
          {membership.dispatchCenter?.name ?? "All centers"}
        </span>
      </div>
      {pending && (
        <p role="status" className="mt-2 text-xs text-muted-foreground">
          Saving access changes…
        </p>
      )}
      <Button
        variant="outline"
        size="touch"
        className="mt-3 w-full"
        onClick={onView}
      >
        View access details
      </Button>
    </article>
  );
}

function MemberDetailSheet({
  entry,
  centers,
  centersLoading,
  centersError,
  pending,
  blocked,
  mutationError,
  onClose,
  onRequestUpdate,
}: {
  entry: MemberEntry;
  centers: CarrierDispatchCenter[];
  centersLoading: boolean;
  centersError: unknown;
  pending: boolean;
  blocked: boolean;
  mutationError: string | null;
  onClose: () => void;
  onRequestUpdate: RequestMembershipUpdate;
}) {
  const { member, membership } = entry;
  return (
    <DetailDrawer
      open
      onOpenChange={(open) => {
        if (!open && !blocked) onClose();
      }}
      title={member.name ?? "Unnamed member"}
      description={member.email}
      closeDisabled={blocked}
      footer={
        <MemberActions
          entry={entry}
          disabled={blocked}
          onRequestUpdate={onRequestUpdate}
          expanded
        />
      }
    >
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Detail label="Phone" value={member.phone ?? "Not provided"} />
        <Detail label="Joined" value={formatDateOnly(member.createdAt)} />
        <Detail label="Account" value={member.active ? "Active" : "Inactive"} />
        <Detail label="Membership" value={membership.status} />
      </dl>
      <div className="mt-5 space-y-4 border-t pt-4">
        <LabeledControl label="Role">
          <RoleControl
            entry={entry}
            disabled={blocked}
            onRequestUpdate={onRequestUpdate}
          />
        </LabeledControl>
        <LabeledControl label="Dispatch center access">
          <CenterControl
            entry={entry}
            centers={centers}
            centersLoading={centersLoading}
            centersError={centersError}
            disabled={blocked}
            onRequestUpdate={onRequestUpdate}
          />
        </LabeledControl>
      </div>
      {pending && (
        <p role="status" className="mt-4 text-sm text-muted-foreground">
          Saving access changes…
        </p>
      )}
      {mutationError && <MutationError message={mutationError} compact />}
    </DetailDrawer>
  );
}

function InviteCard({
  invite,
  pending,
  onView,
}: {
  invite: CarrierTeamInvite;
  pending: boolean;
  onView: () => void;
}) {
  return (
    <article
      className="rounded-md border bg-background p-3"
      aria-busy={pending || undefined}
    >
      <h3 className="break-all text-sm font-semibold">{invite.email}</h3>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        <span>{roleLabel(invite.role)}</span>
        <span className="text-muted-foreground">
          {invite.dispatchCenter?.name ?? "All centers"}
        </span>
      </div>
      {pending && (
        <p role="status" className="mt-2 text-xs text-muted-foreground">
          Updating invitation…
        </p>
      )}
      <Button
        variant="outline"
        size="touch"
        className="mt-3 w-full"
        onClick={onView}
      >
        View invitation
      </Button>
    </article>
  );
}

function InviteActions({
  invite,
  disabled,
  onResend,
  onRevoke,
  expanded = false,
}: {
  invite: CarrierTeamInvite;
  disabled: boolean;
  onResend: () => void;
  onRevoke: () => void;
  expanded?: boolean;
}) {
  if (expanded) {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <Button
          variant="outline"
          size="touch"
          disabled={disabled}
          onClick={onResend}
        >
          Resend invite
        </Button>
        <Button
          variant="outline"
          size="touch"
          disabled={disabled}
          onClick={onRevoke}
        >
          Revoke invite
        </Button>
      </div>
    );
  }
  return (
    <RowMenu
      label={`Actions for invitation ${invite.email}`}
      disabled={disabled}
    >
      <RowMenuItem onSelect={onResend}>Resend invite</RowMenuItem>
      <RowMenuItem onSelect={onRevoke}>Revoke invite</RowMenuItem>
    </RowMenu>
  );
}

function InviteDetailSheet({
  invite,
  pending,
  blocked,
  mutationError,
  onClose,
  onResend,
  onRevoke,
}: {
  invite: CarrierTeamInvite;
  pending: boolean;
  blocked: boolean;
  mutationError: string | null;
  onClose: () => void;
  onResend: () => void;
  onRevoke: () => void;
}) {
  return (
    <DetailDrawer
      open
      onOpenChange={(open) => {
        if (!open && !blocked) onClose();
      }}
      title="Pending invitation"
      description={invite.email}
      closeDisabled={blocked}
      footer={
        <InviteActions
          invite={invite}
          disabled={blocked}
          onResend={onResend}
          onRevoke={onRevoke}
          expanded
        />
      }
    >
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Detail label="Role" value={roleLabel(invite.role)} />
        <Detail
          label="Dispatch center access"
          value={invite.dispatchCenter?.name ?? "All centers"}
        />
        <Detail label="Sent" value={formatDateOnly(invite.createdAt)} />
        <Detail label="Expires" value={formatDateOnly(invite.expiresAt)} />
      </dl>
      {pending && (
        <p role="status" className="mt-4 text-sm text-muted-foreground">
          Updating invitation…
        </p>
      )}
      {mutationError && <MutationError message={mutationError} compact />}
    </DetailDrawer>
  );
}

function AccessConfirmationDialog({
  confirmation,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  confirmation: AccessConfirmation;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onCancel();
      }}
      title={confirmation.title}
      presentation="responsive-sheet"
      closeDisabled={pending}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={onCancel}
            disabled={pending}
          >
            {confirmation.cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmation.destructive ? "destructive" : "default"}
            size="touch"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? "Saving…" : confirmation.confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p>{confirmation.description}</p>
        {pending && (
          <p role="status" className="text-muted-foreground">
            {confirmation.pendingLabel}
          </p>
        )}
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function SectionHeading({
  id,
  icon,
  title,
  description,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 id={id} className="flex items-center gap-2 text-base font-semibold">
        {icon}
        {title}
      </h2>
      <span className="text-xs text-muted-foreground">{description}</span>
    </div>
  );
}

function ListLoading({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="space-y-2 p-3">
      <Skeleton className="h-12" />
      <Skeleton className="h-12" />
      <Skeleton className="h-12" />
    </div>
  );
}

function ListError({
  forbidden,
  subject,
  onRetry,
}: {
  forbidden: boolean;
  subject: string;
  onRetry: () => void;
}) {
  return (
    <CardContent role="alert" className="flex items-start gap-3 p-4">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {forbidden
            ? `Access to ${subject} denied`
            : `Couldn’t load ${subject}`}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {forbidden
            ? "Carrier Admin permission is required."
            : "Check your connection and try again."}
        </p>
      </div>
      {!forbidden && (
        <Button variant="outline" size="sm-touch" onClick={onRetry}>
          Try again
        </Button>
      )}
    </CardContent>
  );
}

function EmptyList({
  icon,
  message,
}: {
  icon: React.ReactNode;
  message: string;
}) {
  return (
    <CardContent role="status" className="p-8 text-center">
      <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center text-muted-foreground">
        {icon}
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </CardContent>
  );
}

function CachedWarning({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 border-b bg-amber-50 p-3 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between"
    >
      <span>{message}</span>
      <Button
        variant="outline"
        size="sm-touch"
        className="bg-background"
        onClick={onRetry}
      >
        Try again
      </Button>
    </div>
  );
}

function MutationError({
  message,
  compact = false,
}: {
  message: string;
  compact?: boolean;
}) {
  return (
    <p
      role="alert"
      className={
        compact
          ? "mt-4 text-sm text-destructive"
          : "border-b bg-destructive/5 p-3 text-sm text-destructive"
      }
    >
      {message}
    </p>
  );
}

function LabeledControl({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium">{label}</div>
      {children}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words font-medium">{value}</dd>
    </div>
  );
}

function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

function accountStatus(
  member: CarrierTeamMember,
  membership: CarrierTeamMembership
): string {
  if (!member.active) return "INACTIVE";
  return membership.status;
}
